/**
 * Desktop search integration. Submenu panels are intentionally inert until
 * their follow-up implementation.
 */
(function() {
  const sectionSelector = '[data-section-type="site-header"]';

  function getSearchApp() {
    const existing = window.theme && window.theme.SearchApp;
    if (existing && typeof existing.showModal === 'function') {
      return existing;
    }

    // Predictive Search custom element
    const predictive = document.querySelector('predictive-search.search');
    if (predictive && typeof predictive.showModal === 'function') {
      window.theme = window.theme || {};
      window.theme.SearchApp = predictive;
      return predictive;
    }

    // Tagalys Vue instance mounted on #search--app
    const tagalysEl = document.querySelector('#search--app');
    const tagalysApp = tagalysEl && tagalysEl.__vue__;
    if (tagalysApp && typeof tagalysApp.showModal === 'function') {
      window.theme = window.theme || {};
      window.theme.SearchApp = tagalysApp;
      return tagalysApp;
    }

    return null;
  }

  function getSearchPanelElement() {
    const searchApp = getSearchApp();
    if (searchApp) {
      if (searchApp.$el) {
        return searchApp.$el;
      }
      if (searchApp.nodeType === 1) {
        return searchApp;
      }
    }
    return (
      document.querySelector('#search--app') ||
      document.querySelector('predictive-search.search')
    );
  }

  function isSearchOpen() {
    const searchApp = getSearchApp();
    return Boolean(
      (searchApp && searchApp.isVisible) ||
      document.body.classList.contains('search-is-visible')
    );
  }

  function getDesktopSearch(section) {
    return section.querySelector('.shd__search[data-shd-search]');
  }

  function syncSearchUiState(section, isOpen) {
    const search = getDesktopSearch(section);
    if (!search) {
      return;
    }
    const input = search.querySelector('[data-shd-search-input]');
    const closeTrigger = search.querySelector('[data-shd-search-close]');

    search.classList.toggle('is-open', isOpen);
    search.classList.toggle('is-focused', isOpen);
    if (input) {
      input.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
    if (closeTrigger) {
      closeTrigger.setAttribute('aria-hidden', isOpen ? 'false' : 'true');
      closeTrigger.tabIndex = isOpen ? 0 : -1;
    }
  }

  function syncDesktopSearchState(search) {
    const input = search && search.querySelector('[data-shd-search-input]');
    if (!input) {
      return;
    }
    search.classList.toggle('has-value', Boolean(input.value && input.value.trim()));
  }

  // Desktop uses the header pill as the only input. SearchApp.showModal adds
  // MainContent/overlay dismiss listeners and focuses the hidden panel field —
  // both cause open/close glitches, so we replace show/hide on desktop only.
  function initDesktopSearchApp() {
    const app = getSearchApp();
    if (!app || app.__shdDesktopMode) {
      return;
    }
    app.__shdDesktopMode = true;

    const nativeShow = app.showModal.bind(app);
    const nativeHide = app.hideModal.bind(app);
    const panelEl = app.$el || app;

    app.showModal = function() {
      if (!window.matchMedia('(min-width: 990px)').matches) {
        nativeShow();
        return;
      }

      window.theme = window.theme || {};
      window.theme.lastActiveElement = null;

      if (!app.bgOverlay) {
        app.bgOverlay = document.querySelector('.bg-overlay');
      }

      document.body.classList.add('search-is-visible');
      app.isVisible = true;
      if (app.bgOverlay) {
        app.bgOverlay.setAttribute('aria-hidden', 'false');
      }
      if (panelEl.setAttribute) {
        panelEl.setAttribute('aria-hidden', 'false');
      }
      if (panelEl.classList) {
        panelEl.classList.add('is-visible');
      }

      if (!app.wasModalShown) {
        if (typeof app.setTrendingSearches === 'function') {
          app.setTrendingSearches();
        }
        const panelInput = app.searchInput || (app.$refs && app.$refs.searchQuery);
        if (panelInput && panelInput.value.length > 1 && typeof app.onSearch === 'function') {
          app.onSearch();
        }
      }
      app.wasModalShown = true;
    };

    app.hideModal = function() {
      if (!window.matchMedia('(min-width: 990px)').matches) {
        nativeHide();
        return;
      }

      window.theme = window.theme || {};
      window.theme.lastActiveElement = null;

      document.body.classList.remove('search-is-visible');
      const panelInput = app.searchInput || (app.$refs && app.$refs.searchQuery);
      if (panelInput) {
        panelInput.blur();
      }
      if (app.bgOverlay) {
        app.bgOverlay.setAttribute('aria-hidden', 'true');
      }
      if (panelEl.setAttribute) {
        panelEl.setAttribute('aria-hidden', 'true');
      }
      if (panelEl.classList) {
        panelEl.classList.remove('is-visible');
      }
      app.isVisible = false;
    };
  }

  function positionSearch(section) {
    const searchElement = getSearchPanelElement();
    const headerBar = section.querySelector('.shd__bar');

    if (searchElement && searchElement.style && headerBar) {
      // Measure AFTER the modal opens: `search-is-visible` sets
      // #shopify-section-announcement { display:none }, so the header rises and
      // its bottom becomes the true, flush offset for the modal. Measuring
      // before opening captured the announcement height as an extra gap.
      const bottom = headerBar.getBoundingClientRect().bottom;
      searchElement.style.top = Math.max(Math.round(bottom), 0) + 'px';
    }
  }

  function getSearchAppInput(app) {
    if (!app) {
      return null;
    }
    if (app.searchInput) {
      return app.searchInput;
    }
    if (app.$refs && app.$refs.searchQuery) {
      return app.$refs.searchQuery;
    }
    const panel = getSearchPanelElement();
    if (panel && panel.querySelector) {
      return (
        panel.querySelector('#search-input-main') ||
        panel.querySelector('input[name="q"]') ||
        panel.querySelector('input[name="query"]')
      );
    }
    return (
      document.querySelector('#search-input-main') ||
      document.querySelector('#search--app input[name="q"]')
    );
  }

  function syncQueryToSearchApp(headerInput) {
    const app = getSearchApp();
    const appInput = getSearchAppInput(app);
    if (!appInput || !headerInput) {
      return;
    }

    const nextValue = headerInput.value;
    if (appInput.value !== nextValue) {
      appInput.value = nextValue;
    }

    try {
      // Tagalys listens for native input events on #search-input-main.
      appInput.dispatchEvent(new Event('input', { bubbles: true }));
      appInput.dispatchEvent(new Event('keyup', { bubbles: true }));
    } catch (err) {
      // IE-safe no-op; modern storefronts support Event constructor.
    }

    // Predictive Search custom element path.
    if (typeof app.onSearch === 'function') {
      app.onSearch();
    }
  }

  function closeSearch(section) {
    if (!section.__shdSearchOpen) {
      return;
    }
    section.__shdSearchOpen = false;

    const searchApp = getSearchApp();
    if (searchApp && typeof searchApp.hideModal === 'function') {
      searchApp.hideModal();
    } else {
      document.body.classList.remove('search-is-visible');
    }

    syncSearchUiState(section, false);

    const input = getDesktopSearch(section) &&
      getDesktopSearch(section).querySelector('[data-shd-search-input]');
    if (input) {
      input.blur();
    }
  }

  function openSearch(section, headerInput) {
    if (section.__shdSearchOpen) {
      syncQueryToSearchApp(headerInput);
      positionSearch(section);
      return;
    }

    initDesktopSearchApp();

    const searchApp = getSearchApp();
    const search = getDesktopSearch(section);
    const input = headerInput || (search && search.querySelector('[data-shd-search-input]'));
    if (!searchApp || !input) {
      return;
    }

    if (typeof searchApp.showModal === 'function') {
      searchApp.showModal();
    } else {
      return;
    }

    section.__shdSearchOpen = true;
    syncSearchUiState(section, true);

    requestAnimationFrame(function() {
      positionSearch(section);
      syncQueryToSearchApp(input);
      input.focus({ preventScroll: true });
    });
  }

  function getActivePlaceholderQuery(search) {
    if (!search || !search.querySelector) {
      return '';
    }
    const active = search.querySelector('[data-ph-word].is-active');
    const fallback = active || search.querySelector('[data-ph-word]');
    if (!fallback) {
      return '';
    }
    return String(fallback.getAttribute('data-label') || fallback.textContent || '').trim();
  }

  function getProviderSearchParam() {
    return window.theme && window.theme.searchProvider === 'tagalys' ? 'query' : 'q';
  }

  function rememberSearchQuery(query) {
    if (!query) {
      return;
    }
    let lastSearches = [];
    try {
      lastSearches = localStorage.getItem('lastSearches')
        ? JSON.parse(localStorage.getItem('lastSearches'))
        : [];
    } catch (err) {
      lastSearches = [];
    }
    lastSearches = lastSearches.filter(function(s) {
      return String(s).toLowerCase() !== query.toLowerCase();
    });
    lastSearches.unshift(query);
    lastSearches = lastSearches.slice(0, 10);
    try {
      localStorage.setItem('lastSearches', JSON.stringify(lastSearches));
    } catch (err) {
      // ignore
    }
  }

  // Self-contained cart count. The theme's global side-cart updater
  // (side-cart.js onCartUpdated) writes the item count into EVERY
  // `.header__cart-count` element — our badge carries that class — so we never
  // read the legacy header's DOM. We only re-derive the empty/aria state from
  // the badge's own value whenever the cart changes.
  function refreshBag(section) {
    // Device-neutral: the badge always carries `.header__cart-count`
    // (desktop `.shd__bag-count` / mobile `.shm__bag-count` both add it).
    const target = section.querySelector('.header__cart-count');
    if (!target) {
      return;
    }

    const count = parseInt(target.textContent, 10) || 0;
    target.classList.toggle('hide', count === 0);

    const bagLink = target.closest('a');
    if (bagLink && bagLink.getAttribute('data-bag-label')) {
      bagLink.setAttribute('aria-label', bagLink.getAttribute('data-bag-label') + ' (' + count + ')');
    }
  }

  function prefersReducedMotion() {
    return Boolean(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  // Rotating, linkable search placeholder (desktop pill only). The header input
  // opens the SearchApp panel; a sibling icon <a> ([data-shd-search-go]) navigates
  // to whichever word is currently shown. Scoped per pill so mobile inline
  // search words are not mixed into this rotator.
  function setupPlaceholder(section) {
    const pill = getDesktopSearch(section);
    if (!pill) {
      return;
    }
    const goLink = pill.querySelector('[data-shd-search-go]');
    const input = pill.querySelector('[data-shd-search-input]');
    const words = pill.querySelectorAll('[data-ph-word]');
    if (!goLink || words.length === 0) {
      return;
    }

    const openSearchLabel = (goLink.getAttribute('aria-label')) || 'Search';
    const searchUrl = goLink.getAttribute('data-search-url') || '/search';

    function syncGo(word) {
      const link = word && word.getAttribute('data-link');
      const label = (word && word.getAttribute('data-label')) || '';
      if (link) {
        goLink.setAttribute('href', link);
        goLink.removeAttribute('data-no-link');
        goLink.setAttribute('aria-label', label ? 'Go to ' + label : openSearchLabel);
      } else {
        goLink.setAttribute('href', searchUrl);
        goLink.setAttribute('data-no-link', '');
        goLink.setAttribute('aria-label', openSearchLabel);
      }
    }

    // Start from whichever word the markup flagged active.
    let index = 0;
    for (let i = 0; i < words.length; i++) {
      if (words[i].classList.contains('is-active')) {
        index = i;
        break;
      }
    }
    syncGo(words[index]);

    // Icon click: no link -> focus the input (panel opens on focus).
    goLink.addEventListener('click', function(event) {
      if (!goLink.hasAttribute('data-no-link')) {
        return;
      }
      event.preventDefault();
      if (input) {
        input.focus();
      }
    });

    // Nothing to rotate: a single word, or the user opted out of motion.
    if (words.length < 2 || prefersReducedMotion()) {
      return;
    }

    let hovering = false;
    pill.addEventListener('mouseenter', function() { hovering = true; });
    pill.addEventListener('mouseleave', function() { hovering = false; });
    pill.addEventListener('focusin', function() { hovering = true; });
    pill.addEventListener('focusout', function() { hovering = false; });

    // Pause while the user is on the pill, while the modal is open, or while the
    // tab is hidden — the interval keeps ticking but skips the swap.
    function paused() {
      return hovering ||
        document.hidden ||
        pill.classList.contains('is-focused') ||
        pill.classList.contains('has-value') ||
        document.body.classList.contains('search-is-visible');
    }

	function getDesktopPhInterval(section) {
		const raw = section && section.getAttribute('data-ph-interval');
		const value = parseInt(raw, 10);
		return value >= 2000 ? value : 4000;
	}

    function advance() {
      if (paused()) {
        return;
      }
      const current = words[index];
      const nextIndex = (index + 1) % words.length;
      const next = words[nextIndex];

      current.classList.remove('is-active');
      current.classList.add('is-leaving');
      // Once it has slid out (opacity 0 throughout), park it back below the box
      // so it's ready to rise again next cycle.
      window.setTimeout(function() {
        current.classList.remove('is-leaving');
      }, 550);

      next.classList.remove('is-leaving');
      next.classList.add('is-active');

      index = nextIndex;
      syncGo(next);
    }

	section.__shdPhTimer = window.setInterval(
      advance,
      getDesktopPhInterval(section)
    );
  }

  function setupDesktopSearch(section) {
    const search = getDesktopSearch(section);
    if (!search || search.__shdSearchBound) {
      return;
    }
    search.__shdSearchBound = true;
    initDesktopSearchApp();

    const input = search.querySelector('[data-shd-search-input]');
    const form = search.querySelector('[data-shd-search-form]');
    const closeBtn = search.querySelector('[data-shd-search-close]');
    const overlay = document.querySelector('.bg-overlay');

    if (input) {
      input.addEventListener('click', function() {
        openSearch(section, input);
      });
      // Keyboard tab — focus fires without a click; defer one tick so mouse
      // click opens first and we don't double-call.
      input.addEventListener('focus', function() {
        window.setTimeout(function() {
          if (!section.__shdSearchOpen && document.activeElement === input) {
            openSearch(section, input);
          }
        }, 0);
      });
      input.addEventListener('input', function() {
        syncDesktopSearchState(search);
        if (section.__shdSearchOpen) {
          syncQueryToSearchApp(input);
        }
      });
    }

    if (form) {
      form.addEventListener('submit', function(event) {
        let query = input ? input.value.trim() : '';
        if (!query && input) {
          query = getActivePlaceholderQuery(search);
          if (query) {
            input.value = query;
            syncDesktopSearchState(search);
            syncQueryToSearchApp(input);
          }
        }

        if (!query) {
          event.preventDefault();
          return;
        }

        rememberSearchQuery(query);

        const desiredParam = getProviderSearchParam();
        if (input && input.name !== desiredParam) {
          event.preventDefault();
          window.location.assign(
            (form.getAttribute('action') || '/search') +
            '?' + desiredParam + '=' + encodeURIComponent(query)
          );
        }
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('mousedown', function(event) {
        event.preventDefault();
      });
      closeBtn.addEventListener('click', function(event) {
        event.preventDefault();
        closeSearch(section);
      });
    }

    if (overlay) {
      overlay.addEventListener('click', function(event) {
        if (!section.__shdSearchOpen) {
          return;
        }
        event.stopPropagation();
        closeSearch(section);
      });
    }

    document.addEventListener('keydown', function(event) {
      if (event.key === 'Escape' && section.__shdSearchOpen) {
        closeSearch(section);
      }
    });

    syncDesktopSearchState(search);
    syncSearchUiState(section, false);
  }

  // Desktop sub-menu (mega) panels. Hovering or focusing a top-level item that
  // has children opens its full-width panel and dims the rest of the page with
  // the shared grey overlay (the same layer the search modal uses). Positioning
  // is pure CSS (the panel is absolute under the bar); JS only toggles state.
  function setupSubmenus(section) {
    const items = section.querySelectorAll('.shd__item[data-has-children="true"]');
    if (!items.length) {
      return;
    }

    const overlay = document.querySelector('.bg-overlay');
    let openItem = null;
    let closeTimer = null;

    function cancelClose() {
      if (!closeTimer) {
        return;
      }
      window.clearTimeout(closeTimer);
      closeTimer = null;
    }

    function setExpanded(item, expanded) {
      const link = item.querySelector('.shd__link');
      if (link) {
        link.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      }
    }

    function openPanel(item) {
      cancelClose();
      if (openItem === item) {
        return;
      }
      const switching = openItem && openItem !== item;
      if (openItem) {
        openItem.classList.remove('is-open');
        setExpanded(openItem, false);
      }
      if (switching) {
        document.body.classList.add('shd-submenu-switching');
      }
      openItem = item;
      item.classList.add('is-open');
      setExpanded(item, true);
      document.body.classList.add('shd-submenu-open');
      if (switching) {
        window.requestAnimationFrame(function() {
          document.body.classList.remove('shd-submenu-switching');
        });
      }
    }

    function closePanel() {
      cancelClose();
      if (!openItem) {
        return;
      }
      openItem.classList.remove('is-open');
      setExpanded(openItem, false);
      openItem = null;
      document.body.classList.remove('shd-submenu-open', 'shd-submenu-switching');
    }

    function scheduleClose() {
      cancelClose();
      // Small grace period so moving between adjacent items doesn't flicker.
      closeTimer = window.setTimeout(closePanel, 120);
    }

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      item.addEventListener('mouseenter', function() {
        openPanel(item);
      });
      item.addEventListener('mouseleave', scheduleClose);
      item.addEventListener('focusin', function() {
        openPanel(item);
      });
      item.addEventListener('focusout', function(event) {
        if (item.contains(event.relatedTarget)) {
          return;
        }
        scheduleClose();
      });
    }

    const onOverlayClick = function() {
      closePanel();
    };
    const onKeydown = function(event) {
      if (event.key === 'Escape') {
        closePanel();
      }
    };
    const onScroll = function() {
      closePanel();
    };

    if (overlay) {
      overlay.addEventListener('click', onOverlayClick);
    }
    document.addEventListener('keydown', onKeydown);
    window.addEventListener('scroll', onScroll, {passive: true});

    section.__shdSubmenuCleanup = function() {
      closePanel();
      if (overlay) {
        overlay.removeEventListener('click', onOverlayClick);
      }
      document.removeEventListener('keydown', onKeydown);
      window.removeEventListener('scroll', onScroll);
    };
  }

  // Desktop bar fitting. The search shrinks first via CSS flex; this adds the
  // wrap class ONLY when the search has bottomed out at its min-width and the
  // menu still overflows one row — then the menu (not the search) wraps to a
  // second row, so the search is never pushed off the right edge.
  function fitHeader(section) {
    const bar = section.querySelector('.shd__bar');
    const menu = section.querySelector('.shd__menu');
    if (!bar || !menu) {
      return;
    }
    // Reset to one row, then measure. Reading scrollWidth forces a layout, so
    // the search has already flex-shrunk to its min before we compare widths.
    menu.classList.remove('shd__menu--wrap');
    if (bar.scrollWidth - bar.clientWidth > 1) {
      menu.classList.add('shd__menu--wrap');
    }
  }

  let fitQueued = false;
  function fitAllHeaders() {
    fitQueued = false;
    const nodes = document.querySelectorAll(sectionSelector);
    for (let i = 0; i < nodes.length; i++) {
      fitHeader(nodes[i]);
    }
    document.dispatchEvent(new CustomEvent('header:metrics-updated'));
  }
  function queueFit() {
    if (fitQueued) {
      return;
    }
    fitQueued = true;
    window.requestAnimationFrame(fitAllHeaders);
  }

  function init(section) {
    if (!section || section.__shdInit) {
      return;
    }
    section.__shdInit = true;

    setupDesktopSearch(section);
    setupPlaceholder(section);
    setupSubmenus(section);
    fitHeader(section);

    if (section.querySelector('.shd__bag-count')) {
      refreshBag(section);
      const onCartChange = function() {
        refreshBag(section);
      };
      // Broadcast by the theme on every cart change (cart.js / side-cart.js).
      document.addEventListener('cart:rendered', onCartChange);
      document.addEventListener('side-cart:rendered', onCartChange);
      section.__shdCartHandler = onCartChange;
    }
  }

  function notifyHeaderMetrics() {
    document.dispatchEvent(new CustomEvent('header:metrics-updated'));
  }

  function boot() {
    const nodes = document.querySelectorAll(sectionSelector);
    for (let i = 0; i < nodes.length; i++) {
      init(nodes[i]);
    }
    notifyHeaderMetrics();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function(event) {
    if (!event.target || !event.target.querySelector) {
      return;
    }
    const node = event.target.matches && event.target.matches(sectionSelector)
      ? event.target
      : event.target.querySelector(sectionSelector);
    init(node);
    notifyHeaderMetrics();
  });

  document.addEventListener('shopify:section:unload', function(event) {
    if (!event.target || !event.target.querySelector) {
      return;
    }
    const node = event.target.matches && event.target.matches(sectionSelector)
      ? event.target
      : event.target.querySelector(sectionSelector);
    if (node && node.__shdCartHandler) {
      document.removeEventListener('cart:rendered', node.__shdCartHandler);
      document.removeEventListener('side-cart:rendered', node.__shdCartHandler);
    }
    if (node && node.__shdPhTimer) {
      window.clearInterval(node.__shdPhTimer);
      node.__shdPhTimer = null;
    }
    if (node && node.__shdSubmenuCleanup) {
      node.__shdSubmenuCleanup();
      node.__shdSubmenuCleanup = null;
    }
  });

  // Re-fit the bar (search shrink, then menu wrap) on resize, and keep the open
  // search modal flush with the header.
  window.addEventListener('resize', function() {
    queueFit();
    if (!document.body.classList.contains('search-is-visible')) {
      return;
    }
    const nodes = document.querySelectorAll(sectionSelector);
    for (let i = 0; i < nodes.length; i++) {
      positionSearch(nodes[i]);
    }
  });

  // Re-fit after all resources load and once web fonts are ready — the menu's
  // width depends on the Futura face, which may arrive after first paint.
  window.addEventListener('load', fitAllHeaders);
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(fitAllHeaders);
  }
})();
